import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { formatPriceINR } from "@/lib/api";

export default function ModelCard({ model, featured = false }) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) setLoaded(true);
  }, []);
  return (
    <Link
      to={`/models/${model.slug}`}
      data-testid={`model-card-${model.slug}`}
      className={`group relative overflow-hidden rounded-3xl border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:shadow-[0_24px_60px_-20px_rgba(15,23,42,0.18)] ${
        featured ? "md:col-span-8" : "md:col-span-4"
      }`}
    >
      <div className="relative h-72 sm:h-80 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center p-4">
        {!loaded && (
          <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-slate-200 to-slate-100" />
        )}
        <img
          ref={imgRef}
          src={model.image}
          alt={model.name}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          className={`relative max-h-full max-w-full object-contain transition-all duration-700 group-hover:scale-105 ${loaded ? "opacity-100" : "opacity-0"}`}
        />
        <span
          data-testid={`model-card-${model.slug}-badge`}
          className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-white/70 backdrop-blur-md border border-white/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-700"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {model.top_speed_kmph} km/h • License-free
        </span>
      </div>

      <div className="p-6 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-950">
              {model.name}
            </h3>
            <p className="mt-1 text-sm text-slate-500 max-w-xs">{model.tagline}</p>
          </div>
          <ArrowUpRight
            className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-emerald-600"
          />
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
          <div className="rounded-xl bg-slate-50 px-2 py-3">
            <p className="font-mono text-base text-slate-950">{model.range_km}</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">km range</p>
          </div>
          <div className="rounded-xl bg-slate-50 px-2 py-3">
            <p className="font-mono text-base text-slate-950">{model.top_speed_kmph}</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">km/h top</p>
          </div>
          <div className="rounded-xl bg-slate-50 px-2 py-3">
            <p className="font-mono text-base text-slate-950">{model.charging_time.split(" ")[0]}</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">hrs charge</p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
            Starting at
          </p>
          <p
            data-testid={`model-card-${model.slug}-price`}
            className="font-heading text-xl text-slate-950"
          >
            {formatPriceINR(model.price_inr)}
          </p>
        </div>
      </div>
    </Link>
  );
}
